import React from 'react';
import { Mail, Phone, MapPin, User } from 'lucide-react';
import { eventData } from '../mockData';

const Contact = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-neutral-900 to-black py-20 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-6xl md:text-7xl font-black mb-6 text-transparent bg-clip-text bg-gradient-to-r from-orange-400 via-amber-500 to-orange-600">
            Contact &amp; Lieu
          </h1>
        </div>

        <div className="grid md:grid-cols-1 gap-8">
          {/* Location Info */}
          <div className="bg-neutral-800/50 backdrop-blur-sm p-8 rounded-xl border-2 border-orange-600/30">
            <div className="flex items-center gap-3 mb-6">
              <MapPin className="w-8 h-8 text-orange-500" />
              <h2 className="text-3xl font-bold text-orange-400">Lieu de l'événement</h2>
            </div>

            {/* Step 1 - Address */}
            <div className="bg-black/50 p-5 rounded-lg border border-orange-500/40 mb-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-8 h-8 rounded-full bg-orange-600 text-white font-bold flex items-center justify-center text-sm">1</div>
                <p className="text-orange-300 font-bold uppercase tracking-wider text-sm">Dirigez vous vers</p>
              </div>
              <a
                href="https://www.google.com/maps/?q=Limburgstirumlaan+56,+3040+Huldenberg"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-address-link"
                className="block hover:text-orange-300 transition-colors"
              >
                <p className="text-xl font-semibold text-white">Limburgstirumlaan 56</p>
                <p className="text-base text-gray-300">3040 Huldenberg</p>
                <p className="text-xs text-orange-400 mt-2 italic">📍 Cliquez pour ouvrir dans Google Maps</p>
              </a>
            </div>

            {/* Step 2 - Follow the map */}
            <div className="bg-black/50 p-5 rounded-lg border border-orange-500/40">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-8 h-8 rounded-full bg-orange-600 text-white font-bold flex items-center justify-center text-sm">2</div>
                <p className="text-orange-300 font-bold uppercase tracking-wider text-sm">Ensuite, suivez le plan pour rejoindre la fête</p>
              </div>
              <div className="rounded-lg overflow-hidden border border-orange-500/30">
                <img
                  src="https://customer-assets.emergentagent.com/job_event-landing-19/artifacts/8ogixa5j_image.png"
                  alt="Plan d'accès au camping"
                  className="w-full h-auto block"
                  data-testid="venue-map"
                />
              </div>
            </div>

            {/* Sur place */}
            <div className="pt-5 mt-5 border-t border-orange-600/30">
              <p className="text-sm text-gray-400 mb-2">🏕️ Sur place</p>
              <p className="text-sm text-gray-300 leading-relaxed">
                Le <span className="text-orange-400 font-semibold">parking est juste à côté</span> de la soirée. Vous êtes libres d'apporter votre <span className="text-orange-400 font-semibold">tente</span> ou de <span className="text-orange-400 font-semibold">dormir dans votre voiture</span>. 🌙
              </p>
            </div>
          </div>
        </div>

        {/* Time Banner - dramatic full-width call-out */}
        <div className="relative my-12 py-12 overflow-hidden">
          {/* Diagonal accent lines */}
          <div className="absolute inset-y-0 left-0 w-1 bg-gradient-to-b from-transparent via-orange-500 to-transparent"></div>
          <div className="absolute inset-y-0 right-0 w-1 bg-gradient-to-b from-transparent via-orange-500 to-transparent"></div>

          <div className="text-center relative z-10">
            <p className="text-sm uppercase tracking-[0.4em] text-orange-500/80 mb-3 font-bold">
              ⚡ Rendez-vous ⚡
            </p>
            <p className="mad-max-title text-8xl md:text-[10rem] leading-none mb-6">
              18H00
            </p>
            <p className="text-xl md:text-2xl text-gray-200 leading-relaxed max-w-3xl mx-auto px-4">
              Soyez là <span className="text-orange-400 font-bold">pile à l'heure</span> — le <span className="text-orange-400 font-bold">jeu commence à 18h30</span>.
            </p>
            <p className="text-base text-gray-400 italic mt-4">
              Ne soyez pas en retard, ce serait dommage de rater ça. 🔥
            </p>
          </div>
        </div>

        {/* Contact Section */}
        <div className="mt-8 bg-neutral-800/50 backdrop-blur-sm p-8 rounded-xl border-2 border-orange-600/30">
          <h2 className="text-3xl font-bold text-orange-400 mb-6 text-center">Nous contacter</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-black/60 p-6 rounded-lg border border-orange-500/30 text-center">
              <Mail className="w-10 h-10 text-orange-500 mx-auto mb-3" />
              <p className="text-sm text-gray-400 mb-1">Email</p>
              <a href={`mailto:${eventData.contact.email}`} className="text-lg font-semibold text-orange-400 hover:text-orange-300 transition-colors break-all">
                {eventData.contact.email}
              </a>
            </div>
            <div className="bg-black/60 p-6 rounded-lg border border-orange-500/30 text-center">
              <Phone className="w-10 h-10 text-orange-500 mx-auto mb-3" />
              <p className="text-sm text-gray-400 mb-1">Téléphone — Rose LS</p>
              <a href={`tel:${eventData.contact.phone}`} className="text-lg font-semibold text-orange-400 hover:text-orange-300 transition-colors">
                {eventData.contact.phone}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
